import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

const categoryDisplayNames: Record<string, string> = {};
categories.forEach((c) => { categoryDisplayNames[c.slug] = c.name; });

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = currentCategory?.name || '全部';

  const filteredComics = useMemo(() => {
    let result = activeSlug === 'all'
      ? [...comics]
      : comics.filter((c) => c.categorySlug === activeSlug);

    switch (sortBy) {
      case 'latest':
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
      default:
        result.sort((a, b) => b.views - a.views);
        break;
    }
    return result;
  }, [activeSlug, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo(0, 0);
  };

  const seoTitle = `${categoryName}漫画 - 韩漫吧免费韩国漫画在线阅读`;
  const seoDesc = `韩漫吧${categoryName}漫画分类，提供大量优质${categoryName}类韩国漫画免费在线阅读，下拉式流畅体验，每日更新热门${categoryName}韩漫。`;

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanba.com' },
          { '@type': 'ListItem', position: 2, name: `${categoryName}漫画`, item: `https://hanmanba.com/category/${activeSlug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${categoryName}漫画 - 韩漫吧`,
        url: `https://hanmanba.com/category/${activeSlug}`,
        description: seoDesc,
        isPartOf: { '@type': 'WebSite', name: '韩漫吧' },
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={seoTitle}
        description={seoDesc}
        keywords={`韩漫吧,${categoryName}漫画,免费漫画,韩国漫画,韩漫在线阅读`}
        canonicalUrl={`https://hanmanba.com/category/${activeSlug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '漫画分类', to: '/category/all' },
                { label: `${categoryName}漫画` },
              ]}
            />
          </div>

          {/* Page Title */}
          <div className="mb-6">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
              {categoryName}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共找到 <strong className="text-primary-600">{filteredComics.length}</strong> 部{categoryName !== '全部' ? categoryName : ''}漫画
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-6 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Sort Options */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-xs text-foreground-500">排序：</span>
            {([
              { value: 'popular' as const, label: '人气', icon: 'ri-fire-line' },
              { value: 'latest' as const, label: '最新', icon: 'ri-time-line' },
              { value: 'rating' as const, label: '评分', icon: 'ri-star-line' },
            ]).map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  sortBy === opt.value
                    ? 'bg-secondary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${opt.icon} text-[10px]`}></i>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Comic Grid */}
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无漫画，去看看其他分类吧</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}